import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';

const TimelineSection = () => {
  const [activeYear, setActiveYear] = useState(2024);

  const timelineData = [
    {
      year: 2020,
      title: "The Spark",
      description: "Founded with a vision to revolutionize hydration through sustainable innovation. Started with extensive research on eco-friendly materials.",
      icon: "Lightbulb",
      color: "from-primary to-accent",
      achievements: ["Market Research", "Team Formation", "Initial Funding"]
    },
    {
      year: 2021,
      title: "First Innovation",
      description: "Launched our first sustainable bottle line using recycled materials. Achieved carbon-neutral manufacturing process.",
      icon: "Rocket",
      color: "from-accent to-success",
      achievements: ["First Product Launch", "Carbon Neutral", "1000+ Customers"]
    },
    {
      year: 2022,
      title: "Smart Technology",
      description: "Introduced smart hydration tracking technology. Partnered with leading environmental organizations for impact measurement.",
      icon: "Smartphone",
      color: "from-success to-primary",
      achievements: ["Smart Features", "Partnerships", "10K+ Users"]
    },
    {
      year: 2023,
      title: "Global Expansion",
      description: "Expanded to international markets. Launched premium insulated collection with advanced temperature control technology.",
      icon: "Globe",
      color: "from-primary to-secondary",
      achievements: ["Global Launch", "Premium Line", "50K+ Customers"]
    },
    {
      year: 2024,
      title: "Future Vision",
      description: "Leading the industry in sustainable hydration solutions. Developing next-generation materials and smart features.",
      icon: "Zap",
      color: "from-secondary to-accent",
      achievements: ["Industry Leader", "Next-Gen Tech", "100K+ Community"]
    }
  ];

  return (
    <section className="py-20 bg-background">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <div className="flex items-center justify-center space-x-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center">
                <Icon name="Clock" size={24} color="white" />
              </div>
              <h2 className="text-3xl md:text-4xl font-poppins font-bold text-text-primary">
                Our Journey
              </h2>
            </div>
            <p className="text-lg text-text-secondary font-inter max-w-3xl mx-auto leading-relaxed">
              From a simple idea to a global movement. Discover the milestones that shaped Mava into the premium sustainable brand it is today.
            </p>
          </motion.div>

          {/* Timeline Navigation */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {timelineData?.map((item) => (
              <motion.button
                key={item?.year}
                onClick={() => setActiveYear(item?.year)}
                className={`px-6 py-3 rounded-full font-inter font-medium brand-transition ${
                  activeYear === item?.year
                    ? 'bg-primary text-primary-foreground liquid-shadow'
                    : 'bg-surface text-text-secondary hover:bg-muted hover:text-text-primary'
                }`}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                {item?.year}
              </motion.button>
            ))}
          </div>

          {/* Timeline Content */}
          <div className="relative">
            {/* Timeline Line */}
            <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-primary via-accent to-success rounded-full opacity-20" />

            <div className="space-y-16">
              {timelineData?.map((item, index) => (
                <motion.div
                  key={item?.year}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: index * 0.2 }}
                  className={`relative ${
                    index % 2 === 0 ? 'lg:pr-1/2' : 'lg:pl-1/2 lg:ml-auto'
                  }`}
                >
                  {/* Timeline Node */}
                  <div className="hidden lg:block absolute top-8 left-1/2 transform -translate-x-1/2 w-16 h-16 bg-background rounded-full border-4 border-primary flex items-center justify-center liquid-shadow">
                    <div className={`w-8 h-8 bg-gradient-to-br ${item?.color} rounded-full flex items-center justify-center`}>
                      <Icon name={item?.icon} size={16} color="white" />
                    </div>
                  </div>

                  {/* Content Card */}
                  <div className={`bg-surface rounded-2xl p-8 liquid-shadow hover:shadow-lg brand-transition ${
                    index % 2 === 0 ? 'lg:mr-12' : 'lg:ml-12'
                  }`}>
                    <div className="flex items-start space-x-4 mb-6">
                      <div className={`w-16 h-16 bg-gradient-to-br ${item?.color} rounded-xl flex items-center justify-center flex-shrink-0 lg:hidden`}>
                        <Icon name={item?.icon} size={24} color="white" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <span className="text-2xl font-poppins font-bold text-primary">
                            {item?.year}
                          </span>
                          <div className="h-px bg-border flex-1" />
                        </div>
                        <h3 className="text-xl md:text-2xl font-poppins font-bold text-text-primary mb-3">
                          {item?.title}
                        </h3>
                      </div>
                    </div>

                    <p className="text-text-secondary font-inter leading-relaxed mb-6">
                      {item?.description}
                    </p>

                    {/* Achievements */}
                    <div className="space-y-3">
                      <h4 className="font-poppins font-semibold text-text-primary">Key Achievements:</h4>
                      <div className="flex flex-wrap gap-2">
                        {item?.achievements?.map((achievement, idx) => (
                          <span
                            key={idx}
                            className="px-3 py-1 bg-primary/10 text-primary text-sm font-inter font-medium rounded-full"
                          >
                            {achievement}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Active Year Highlight */}
                    {activeYear === item?.year && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="mt-6 p-4 bg-gradient-to-r from-primary/10 to-accent/10 rounded-xl border border-primary/20"
                      >
                        <div className="flex items-center space-x-2">
                          <Icon name="Star" size={16} color="var(--color-primary)" />
                          <span className="text-primary font-inter font-medium text-sm">
                            Currently Highlighted Milestone
                          </span>
                        </div>
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Future Vision */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mt-20 text-center"
          >
            <div className="bg-gradient-to-r from-primary/5 via-accent/5 to-success/5 rounded-2xl p-8 md:p-12">
              <div className="w-16 h-16 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name="Telescope" size={24} color="white" />
              </div>
              <h3 className="text-2xl md:text-3xl font-poppins font-bold text-text-primary mb-4">
                Looking Ahead
              </h3>
              <p className="text-text-secondary font-inter leading-relaxed max-w-3xl mx-auto">
                Our journey continues with ambitious plans for 2025 and beyond. We're developing revolutionary materials, expanding our smart technology ecosystem, and building partnerships that will reshape the future of sustainable hydration.
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default TimelineSection;